"use client";
import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFacebook,
  faGithub,
  faInstagram,
  faLinkedin
} from "@fortawesome/free-brands-svg-icons";
import { useTranslation } from "react-i18next";
import TransparencyModal from "./TransparencyModal";

const navigation = {
  legal: [
    { name: "Privacy policy", href: "https://www.iubenda.com/privacy-policy/23793780" },
    { name: "Trasparenza", isModal: true },
  ],
  social: [
    {
      name: "Facebook",
      href: "https://www.facebook.com/beehivesud/",
      icon: faFacebook,
    },
    {
      name: "Instagram",
      href: "https://www.instagram.com/beehivesud/",
      icon: faInstagram,
    },
    {
      name: "Linkedin",
      href: "https://it.linkedin.com/company/beehive-valore-sud",
      icon: faLinkedin,
    },
    {
      name: "GitHub",
      href: "https://github.com/beehivesud",
      icon: faGithub,
    },
  ],
};

const addresses = [
  {
    title: "legalAddress",
    lines: ["Via Dei Cedri, 14", "91100 Trapani (TP)"],
  },
  {
    title: "operationalAddress",
    lines: ["Corso Vittorio Emanuele, 42", "91100 Trapani (TP)"],
  },
];

const supporters = [
  {
    src: "/partners/fondazioneconilsud-logo.png",
    alt: "Fondazione con il Sud",
    width: 120,
    height: 40,
  },
  {
    src: "/partners/fc-logo-dark.png",
    alt: "Fondazione Comunitaria di Agrigento e Trapani",
    width: 140,
    height: 40,
  },
];

export default function Footer() {
  const { t } = useTranslation("common");
  const [isTransparencyOpen, setIsTransparencyOpen] = useState(false);

  return (
    <div>
      <footer className="bg-white">
        <div className="mx-auto max-w-7xl px-6 pt-16 pb-8 lg:px-8">
          <div className="space-y-8">
            <Link
              href="/"
              className="flex items-center gap-2 -ml-[19px]"
              aria-label={t("footer.homeAriaLabel")}
            >
              <Image
                src="/loghi/BeehiveLogo.png"
                alt="BeeHive logo"
                width={120}
                height={120}
              />
            </Link>
          </div>
          <div className="md:grid md:grid-cols-3 md:gap-8 mt-10">
            <div className="space-y-8">
              {addresses.map((address) => (
                <div key={address.title}>
                  <h3 className="font-manrope font-semibold text-gray-900">
                    {t(`footer.${address.title}`)}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {address.lines[0]}
                    <br />
                    {address.lines[1]}
                  </p>
                </div>
              ))}
            </div>
            <div className="mt-10 md:mt-0">
              <h3 className="text-sm/6 font-semibold text-gray-900">
                {t("footer.legalTitle")}
              </h3>
              <ul role="list" className="mt-6 space-y-4">
                {navigation.legal.map((item) => (
                  <li key={item.name}>
                    {item.isModal ? (
                      <button
                        type="button"
                        onClick={() => setIsTransparencyOpen(true)}
                        className="cursor-pointer text-sm/6 text-gray-600 hover:text-gray-900"
                      >
                        {t(`footer.legal.${item.name.replace(/\s+/g, "")}`)}
                      </button>
                    ) : (
                      <a
                        href={item.href}
                        className="text-sm/6 text-gray-600 hover:text-gray-900"
                      >
                        {t(`footer.legal.${item.name.replace(/\s+/g, "")}`)}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
            <div className="space-y-4">
              <p className="text-sm">{t("footer.supportedBy")}</p>
              <div className="flex items-center gap-6 flex-wrap justify-between">
                {supporters.map((supporter) => (
                  <Image
                    key={supporter.src}
                    src={supporter.src}
                    alt={supporter.alt}
                    width={supporter.width}
                    height={supporter.height}
                  />
                ))}
              </div>
            </div>
          </div>
          <div className="mt-16 border-t border-gray-900/10 pt-8 sm:mt-20 md:flex md:items-center md:justify-between lg:mt-24">
            <div className="flex gap-x-6 md:order-2 justify-between">
              {navigation.social.map((item) => (
                <a
                  key={item.name}
                  href={item.href}
                  className="text-gray-600 hover:text-gray-800"
                  aria-label={item.name}
                >
                  <span className="sr-only">{item.name}</span>
                  <FontAwesomeIcon icon={item.icon} size="xl" />
                </a>
              ))}
            </div>
            <p className="mt-8 text-sm/6 text-gray-600 md:order-1 md:mt-0 text-center">
              &copy; 2021 - {new Date().getFullYear()} {t("footer.copyright")} |
              P.IVA 02794200812
            </p>
          </div>
        </div>
      </footer>
      <TransparencyModal
        open={isTransparencyOpen}
        onClose={() => setIsTransparencyOpen(false)}
      />
    </div>
  );
}
